import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Grid,
    List,
    ListItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core";
import { useGetRegistrosPlusConfig } from "../../../../hooks/useGetRegistrosPlusConfig";
import { AtCircularProgress } from "../../../../components/AtCircularProgress";
import { FormRegistros } from "./FormRegistros";
import { Alert } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import AddIcon from "@mui/icons-material/Add";
import { getApiKeys } from "../../../../utils/fns/get-api-keys";
import { api_host } from "../../../../common/globals/constants";
import { toast } from "react-toastify";
import { withTranslation } from 'react-i18next';
import { HelpPopover } from "./HelpPopover";

export const Form = withTranslation()(function Form({ objetivoId, servicioId, t }) {
    const { data, isLoading, isError, refetch } = useGetRegistrosPlusConfig({
        objetivoId,
        servicioId,
    });
    const [selectedPaso, setSelectedPaso] = React.useState(null);
    const [isSendingData, setIsSendingData] = React.useState(false);
    const profileId = sessionStorage.getItem("profile_id");

    if (isLoading) {
        return <AtCircularProgress />;
    }

    if (isError) {
        return (
            <Alert severity="error">
                {t('formRegistros.error.loading')}
                {" "}
                <Button variant="text" onClick={refetch} size="small">
                    {t('formRegistros.button.retry')}
                </Button>
            </Alert>
        );
    }

    if (!isLoading && !data.length && objetivoId) {
        return (
            <Alert severity="info">
                {t('formRegistros.info.noSteps')}
            </Alert>
        );
    }

    if (!objetivoId && !isLoading) {
        return <Alert severity="info">{t('formRegistros.info.selectObjective')}</Alert>;
    }

    const filteredSteps = [...data]?.map(step => {
        let registrosMostrar = []
        if (step.registros && Array.isArray(step.registros)) {
            registrosMostrar = step.registros.map(item => ({id: uuidv4(), ...item})).filter(reg => {
                if ('@nombre' in reg && profileId !== '1') {
                    // Se ignoran registros cuyo nombre comienza con "atinterno_"
                    return !reg['@nombre'].startsWith('atinterno_');
                }
                return true;
            })
        }
        return { ...step, registrosMostrar: registrosMostrar }
    })

    function checkValorRegistroEsObjetivo(value) {
        return value instanceof Object && value.constructor === Object;
    }

    function addRegistroPaso() {
        const copiaSelectedPaso = { ...selectedPaso };
        copiaSelectedPaso.registrosMostrar.push({
            id: uuidv4(),
            "@orden": 0,
            "@nombre": "",
            "@objetivo": "",
            "@posicion": "",
            "@tipo": "",
            "@tipo_salida": "",
            "#text": "",
        });
        setSelectedPaso(copiaSelectedPaso);
    }

    function handleSelectPaso(item) {
        const copiaSelectedPaso = { ...item };
        setSelectedPaso(copiaSelectedPaso);
    }

    function eliminarRegistroPaso(id) {
        const copiaSelectedPaso = { ...selectedPaso };
        const registros = copiaSelectedPaso.registrosMostrar.filter(
            (registro) => registro["id"] !== id
        );
        copiaSelectedPaso.registrosMostrar = registros;
        setSelectedPaso(copiaSelectedPaso);
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setIsSendingData(true);
        let textos = event.target.elements.texto;
        let nombres = event.target.elements.nombre;
        let objetivos = event.target.elements.objetivo;
        let posicion = event.target.elements.posicion;
        let tipos = event.target.elements.tipo;
        if (textos && !textos.length) {
            textos = [textos];
            nombres = [nombres];
            objetivos = [objetivos];
            posicion = [posicion];
            tipos = [tipos];
        }
        const registros = [];
        if (textos) {
            for (let i = 0; i < textos.length; i++) {
                registros.push({
                    "@orden": i,
                    "#text": `<![CDATA[${textos[i].value}]]>`,
                    "@nombre": nombres[i].value,
                    "@objetivo": isAtBrowser ? objetivos[i].value : "",
                    "@posicion": posicion[i].value,
                    "@tipo": tipos[i].value,
                });
            }
        }

        if (profileId !== '1') {
            const dataRegistrosPlusApmNoAdmin = selectedPaso.registros.filter(registro => {
                return registro['@nombre'] && registro['@nombre'].startsWith('atinterno_');
            });
            dataRegistrosPlusApmNoAdmin.forEach(registro => {
                registros.push({
                    "@orden": registro['@orden'],
                    "#text": `<![CDATA[${registro['#text']}]]>`,
                    "@nombre": registro['@nombre'],
                    "@objetivo": registro['@objetivo'],
                    "@posicion": registro['@posicion'],
                    "@tipo": registro['@tipo'],
                });
            })
        }

        try {
            const { token } = getApiKeys();
            const response = await toast.promise(
                fetch(
                    `${api_host}/reporte/general/objetivos/${objetivoId}/pasos_config_obj/?token=${token}`,
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            registros,
                            token,
                            paso_orden: selectedPaso.paso_orden,
                        }),
                        headers: { "Content-Type": "application/json" },
                    }
                ),
                { pending: t('formRegistros.toast.pending') }
            );
            if (!response.ok) {
                throw new Error("Error al guardar los datos del form");
            }
            toast.success(t('formRegistros.toast.saveSuccess'));
            setSelectedPaso(null);
            refetch();
        } catch (err) {
            toast.error(err.message ?? t('formRegistros.toast.retryLater'));
        } finally {
            setIsSendingData(false);
        }
    }

    function handleClose() {
        setSelectedPaso(null);
    }

    const showModalEditarRegistros = Boolean(selectedPaso);

    const isAtBrowser =
        (Number(servicioId) >= 250 && Number(servicioId) <= 259) ||
        (Number(servicioId) >= 270 && Number(servicioId) <= 279);

    const checkTipoRegistroIsValid = () => {
        if (selectedPaso) {
            for (let registro of selectedPaso.registros) {
                if (
                    registro["@tipo"] !== "" &&
                    !["javascript", "regexp"].includes(registro["@tipo"])
                ) {
                    return false;
                }
            }
        }
        return true;
    };

    return (
        <Box>
            <Box style={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('formRegistros.table.header.step')}</TableCell>
                            <TableCell>{t('formRegistros.table.header.action')}</TableCell>
                            <TableCell>{t('formRegistros.table.header.records')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredSteps.map((item) => (
                            <TableRow key={`${item.paso_orden}-${item.paso_nombre}`}>
                                <TableCell>{item.paso_nombre}</TableCell>
                                <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleSelectPaso(item)}
                                        >
                                        {t('formRegistros.button.records')}
                                        </Button>
                                </TableCell>
                                <TableCell>
                                    {item.registrosMostrar.length === 0 && (
                                        <Typography variant="body1">
                                            {t('formRegistros.info.noRecords')}
                                        </Typography>
                                    )}
                                    <List disablePadding>
                                        {item.registrosMostrar.map(
                                            (registro, index) => (
                                                <ListItem
                                                    key={index}
                                                    style={{ padding: 4 }}
                                                >
                                                    {Object.entries(
                                                        registro
                                                    ).map(([key, value]) => (
                                                        <>
                                                            {checkValorRegistroEsObjetivo(
                                                                value
                                                            ) ? (
                                                                Object.entries(
                                                                    value
                                                                ).map(
                                                                    ([
                                                                        key2,
                                                                        value2,
                                                                    ]) => (
                                                                        <Typography
                                                                            key={`${key2}-${value2}`}
                                                                            style={{
                                                                                marginRight: 4,
                                                                                display: key2 === 'id' ? 'none' : 'inline',
                                                                            }}
                                                                            variant="body1"
                                                                        >
                                                                            <b>
                                                                                {key2
                                                                                    .replace(
                                                                                        "#",
                                                                                        ""
                                                                                    )
                                                                                    .replace(
                                                                                        "@",
                                                                                        ""
                                                                                    )}

                                                                                :{" "}
                                                                            </b>
                                                                            {`${value2}; `}
                                                                        </Typography>
                                                                    )
                                                                )
                                                            ) : (
                                                                <Typography
                                                                    key={`${key}-${value}`}
                                                                    style={{
                                                                        marginRight: 4,
                                                                        display: key === 'id' ? 'none' : 'inline',
                                                                    }}
                                                                    variant="body1"
                                                                >
                                                                    <b>
                                                                        {key
                                                                            .replace(
                                                                                "#",
                                                                                ""
                                                                            )
                                                                            .replace(
                                                                                "@",
                                                                                ""
                                                                            )}
                                                                        :{" "}
                                                                    </b>
                                                                    {`${value}; `}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    ))}
                                                </ListItem>
                                            )
                                        )}
                                    </List>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <Dialog
                open={showModalEditarRegistros}
                maxWidth="sm"
                fullWidth
                onClose={handleClose}
            >
                {selectedPaso && (
                    <React.Fragment>
                        <DialogTitle disableTypography>
                            <Typography variant="h4">
                                {selectedPaso.paso_nombre}
                            </Typography>
                        </DialogTitle>
                        <Box padding={2} paddingTop={0}>
                            <HelpPopover />
                            {checkTipoRegistroIsValid() ? (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2}>
                                        {selectedPaso.registrosMostrar.length ===
                                            0 && (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={12}
                                            >
                                                <Typography variant="body1">
                                                    {t('plus-records.no-records')}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {selectedPaso.registrosMostrar.map(
                                            (registro) => (
                                                <FormRegistros
                                                    key={registro["id"]}
                                                    id={registro["id"]}
                                                    pasoOrden={
                                                        selectedPaso.paso_orden
                                                    }
                                                    registroTexto={
                                                        registro["#text"]
                                                    }
                                                    registroNombre={
                                                        registro["@nombre"]
                                                    }
                                                    registroObjetivo={
                                                        registro["@objetivo"]
                                                    }
                                                    registroPosicion={
                                                        registro["@posicion"]
                                                    }
                                                    registroTipo={
                                                        registro["@tipo"]
                                                    }
                                                    registroTipoSalida={
                                                        registro["@tipo_salida"]
                                                    }
                                                    eliminarRegistroPaso={
                                                        eliminarRegistroPaso
                                                    }
                                                    isAtBrowser={isAtBrowser}
                                                />
                                            )
                                        )}
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            lg={12}
                                        >
                                            <Tooltip
                                                title={t('formRegistros.tooltip.addRecord')}
                                                placement="bottom"
                                            >
                                                <Button
                                                    onClick={addRegistroPaso}
                                                    color="primary"
                                                    variant="outlined"
                                                    fullWidth
                                                >
                                                    <AddIcon />
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                    <DialogActions>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            disabled={isSendingData}
                                        >
                                            {t('formRegistros.button.saveChanges')}
                                        </Button>
                                    </DialogActions>
                                </form>
                            ) : (
                                <Alert severity="info">
                                    {t('formRegistros.info.contactSupport')}
                                </Alert>
                            )}
                        </Box>
                    </React.Fragment>
                )}
            </Dialog>
        </Box>
    );
});
